import {
  centsFrom,
  formatMoney,
  installmentsAt,
  installmentsOf,
  lastInstallmentOn,
  SUGGESTED_INSTALLMENTS,
  typedFrom,
} from '@app/shared'
import { readCivilDate } from '../lib/today'
import { Choice } from './Choice'
import {
  DateInput,
  Field,
  FieldRow,
  MoneyInput,
  Notice,
  TextInput,
} from './index'
import { Select } from './Select'
import { TotalLine } from './TotalLine'
import './plan-fields.css'

/** How a total is settled: how many payments, how often, and from when. */
export type PlanShape = {
  by: 'count' | 'amount'
  /** One payment is settled in one go, and raises no plan. */
  installments: string
  /** The dollars typed for each payment when the amount decides the plan. */
  rate: string
  cadence: 'monthly' | 'annual'
  startsOn: string
}

/**
 * The shape of a plan, as the office decides it: how many payments, how often,
 * and when the first one falls.
 *
 * One component, because the office answers this question in two places — when
 * it originates a pledge, and when it confirms a visitor's — and a count and a
 * cadence spelled two ways would be two forms free to compute the figure per
 * payment differently. That figure is the one a family checks against their
 * bank.
 *
 * The refusal is the CALLER's, because what it is measured against is the
 * amount that caller is about to save: `whyShapeRefused` in `@app/shared` is
 * the one bound, and passing its answer in keeps this component from holding a
 * second opinion about the figure.
 */
export function PlanFields({
  coversCents,
  value,
  refused,
  chooseBy = true,
  onChange,
}: {
  /** What this plan settles, after anything already paid is left out. */
  coversCents: number
  value: PlanShape
  /** Why the plan cannot stand, from the shared bound. */
  refused: string | null
  /** Visitor proposals store a count and cannot offer an amount-shaped plan. */
  chooseBy?: boolean
  onChange: (next: PlanShape) => void
}) {
  const typedCount = Number(value.installments)
  const typedRate = centsFrom(value.rate)
  const split =
    refused === null
      ? value.by === 'count' && Number.isInteger(typedCount) && typedCount > 0
        ? {
            installments: typedCount,
            ...installmentsOf(coversCents, typedCount),
          }
        : value.by === 'amount' && typedRate !== null && typedRate > 0
          ? installmentsAt(coversCents, typedRate)
          : null
      : null
  const count = split?.installments ?? typedCount
  const spread = Number.isInteger(count) && count > 1
  const endsOn =
    split && spread && /^\d{4}-\d{2}-\d{2}$/.test(value.startsOn)
      ? lastInstallmentOn(value.startsOn, value.cadence, count)
      : null

  const setBy = (by: PlanShape['by']) => {
    if (by === value.by) return
    if (by === 'amount') {
      const n = Number(value.installments)
      const rate =
        Number.isInteger(n) && n > 0
          ? typedFrom(installmentsOf(coversCents, n).rateCents)
          : value.rate
      onChange({ ...value, by, rate })
      return
    }
    const rateCents = centsFrom(value.rate)
    const installments =
      rateCents !== null && rateCents > 0
        ? String(installmentsAt(coversCents, rateCents).installments)
        : value.installments
    onChange({ ...value, by, installments })
  }

  return (
    <>
      {chooseBy && (
        <Choice<PlanShape['by']>
          label="Set the plan by"
          appearance="chip"
          value={value.by}
          options={[
            { value: 'count', label: 'Number of payments' },
            { value: 'amount', label: 'Amount each' },
          ]}
          onValueChange={setBy}
        />
      )}
      {value.by === 'count' ? (
        <>
          <Choice
            label="How many payments?"
            appearance="chip"
            value={
              SUGGESTED_INSTALLMENTS.some(
                (n) => String(n) === value.installments,
              )
                ? value.installments
                : ''
            }
            options={SUGGESTED_INSTALLMENTS.map((n) => ({
              value: String(n),
              label: String(n),
            }))}
            onValueChange={(installments) =>
              onChange({ ...value, installments })
            }
          />
          <Field
            label="Or another number"
            info="One is a pledge settled in a single payment, which raises no plan."
          >
            <TextInput
              type="number"
              inputMode="numeric"
              min={1}
              value={value.installments}
              aria-invalid={refused !== null || undefined}
              onChange={(e) =>
                onChange({ ...value, installments: e.target.value })
              }
            />
          </Field>
        </>
      ) : (
        <Field label="Each payment">
          <MoneyInput
            min={1}
            value={value.rate}
            aria-invalid={refused !== null || undefined}
            onChange={(e) => onChange({ ...value, rate: e.target.value })}
          />
        </Field>
      )}
      <FieldRow cols={spread ? 2 : 1}>
        {/* A cadence is a question about a plan, and one payment raises none.
            The chosen cadence stays in the value, so a count that comes back
            above one comes back to it. */}
        {spread && (
          <Field label="How often">
            <Select
              value={value.cadence}
              onValueChange={(cadence: 'monthly' | 'annual') =>
                onChange({ ...value, cadence })
              }
              options={[
                { value: 'monthly', label: 'Monthly' },
                { value: 'annual', label: 'Yearly' },
              ]}
            />
          </Field>
        )}
      </FieldRow>
      {spread && (
        <Field
          label="First payment"
          info="When the first installment falls. The rest follow at the cadence above."
        >
          <DateInput
            value={value.startsOn}
            onChange={(e) => onChange({ ...value, startsOn: e.target.value })}
          />
        </Field>
      )}
      {refused ? (
        <Notice tone="error">{refused}</Notice>
      ) : split && spread ? (
        <div className="plan-fields__total">
          {/* The arithmetic as the family reads it. A last payment that differs
              is said here, because it is the one figure the count does not
              give. */}
          <TotalLine
            label={`${count} ${value.cadence === 'annual' ? 'yearly' : 'monthly'} payments`}
            amount={formatMoney(split.rateCents)}
          />
          {split.lastCents !== split.rateCents && (
            <TotalLine
              label="Last payment"
              amount={formatMoney(split.lastCents)}
            />
          )}
          {/* When it ends is what a family plans around, and the count alone
              leaves them to work it out. */}
          {endsOn && <TotalLine label="Ends" amount={readCivilDate(endsOn)} />}
          <TotalLine
            label="Total"
            amount={formatMoney(coversCents)}
            emphasis="total"
          />
        </div>
      ) : (
        <Notice>Settled in one payment. No plan is raised.</Notice>
      )}
    </>
  )
}
